import { useEffect, useState } from 'react'
import { getCoursePreviewTokenService } from 'src/api/course'
import URL from 'src/api/url'

/**
 * The learner-app URL for a course, carrying a preview token while the course is still a draft.
 *
 * The learner app runs on its own origin with no admin session, so a draft course is simply not
 * found there — the right to see it has to travel in the URL. Every course starts as a draft, which
 * is why the buttons that open a freshly created or not-yet-published course all need this rather
 * than a bare `APP_URL + code`.
 *
 * Returns the bare URL first and upgrades it when the token arrives, so a slow or failed token
 * request degrades to the old behaviour instead of leaving a dead button.
 */
export function useCoursePreviewUrl(course) {
  const [url, setUrl] = useState('')

  useEffect(() => {
    if (!course?.code) {
      setUrl('')
      return
    }

    const base = URL.APP_URL + course.code

    setUrl(base)

    // Published: there is nothing to unlock, so no round trip.
    if (course.isPublished) {
      return
    }

    let cancelled = false

    getCoursePreviewTokenService(course.id)
      .then(response => {
        // Same { data, statusCode } envelope the rest of the API uses.
        const token = (response?.data ?? response)?.token

        if (!cancelled && token) {
          setUrl(`${base}?previewToken=${encodeURIComponent(token)}`)
        }
      })
      .catch(() => {
        // Leave the bare URL in place.
      })

    return () => {
      cancelled = true
    }
  }, [course?.id, course?.code, course?.isPublished])

  return url
}

export default useCoursePreviewUrl
